'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop, type PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './cover-crop-modal.css';
import { X, Crop as CropIcon, Check } from 'lucide-react';
import { Z } from '@/lib/z-index';
import { useAnimatedClose } from '@/lib/use-animated-close';

/* ── Aspect ratio modes ── */
type AspectMode = 'original' | 'free' | '1:1' | '3:4' | '4:3' | '9:16' | '16:9';

interface AspectPreset {
	label: string;
	mode: AspectMode;
	/** Fixed numeric ratio (width/height). null = derive from image */
	value: number | null;
}

const ASPECT_PRESETS: AspectPreset[] = [
	{ label: '原始', mode: 'original', value: null },
	{ label: '自由', mode: 'free', value: null },
	{ label: '1:1', mode: '1:1', value: 1 },
	{ label: '3:4', mode: '3:4', value: 3 / 4 },
	{ label: '4:3', mode: '4:3', value: 4 / 3 },
	{ label: '9:16', mode: '9:16', value: 9 / 16 },
	{ label: '16:9', mode: '16:9', value: 16 / 9 },
];

const DEFAULT_MODE: AspectMode = 'free';

/** Output max width (px) */
const OUTPUT_MAX_WIDTH = 1200;

/** Load an image from a URL into an HTMLImageElement */
function createImage(url: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.addEventListener('load', () => resolve(img));
		img.addEventListener('error', (e) => reject(e));
		img.crossOrigin = 'anonymous';
		img.src = url;
	});
}

/** Crop the image using canvas and return a Blob.
 *  displayWidth/displayHeight = the rendered size of the <img> inside ReactCrop,
 *  so we can map pixel-crop coords (which are relative to the displayed size)
 *  back to the image's natural resolution. */
async function getCroppedBlob(imageSrc: string, pixelCrop: PixelCrop, displayWidth: number, displayHeight: number): Promise<Blob> {
	const image = await createImage(imageSrc);

	const scaleX = image.naturalWidth / displayWidth;
	const scaleY = image.naturalHeight / displayHeight;

	const srcX = pixelCrop.x * scaleX;
	const srcY = pixelCrop.y * scaleY;
	const srcW = pixelCrop.width * scaleX;
	const srcH = pixelCrop.height * scaleY;

	const outW = Math.min(OUTPUT_MAX_WIDTH, Math.round(srcW));
	const scale = outW / srcW;
	const outH = Math.round(srcH * scale);

	const canvas = document.createElement('canvas');
	canvas.width = outW;
	canvas.height = outH;
	const ctx = canvas.getContext('2d')!;

	ctx.drawImage(image, srcX, srcY, srcW, srcH, 0, 0, outW, outH);

	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (blob) resolve(blob);
				else reject(new Error('Canvas toBlob failed'));
			},
			'image/jpeg',
			0.92,
		);
	});
}

/** Build a full-image crop for "原始" and "自由" modes */
function makeFullCrop(_width: number, _height: number): PercentCrop {
	return { unit: '%', x: 0, y: 0, width: 100, height: 100 };
}

/** Build a centered aspect crop */
function makeCenteredAspectCrop(aspect: number, imgWidth: number, imgHeight: number): PercentCrop {
	return centerCrop(makeAspectCrop({ unit: '%', width: 90 }, aspect, imgWidth, imgHeight), imgWidth, imgHeight);
}

interface CoverCropModalProps {
	imageSrc: string;
	onConfirm: (croppedFile: File, previewUrl: string) => void;
	onCancel: () => void;
}

/**
 * CoverCropModal — Draggable & resizable crop box.
 * Presets: 原始 / 自由 / 1:1 / 3:4 / 4:3 / 9:16 / 16:9 (default: 自由)
 */
export function CoverCropModal({ imageSrc, onConfirm, onCancel }: CoverCropModalProps) {
	const { closing, requestClose } = useAnimatedClose(onCancel, 280);
	const [entered, setEntered] = useState(false);

	const imgRef = useRef<HTMLImageElement>(null);
	const [crop, setCrop] = useState<Crop>();
	const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
	const [aspectMode, setAspectMode] = useState<AspectMode>(DEFAULT_MODE);
	const [processing, setProcessing] = useState(false);

	/* ── Derive aspect for ReactCrop ── */
	const currentPreset = ASPECT_PRESETS.find((p) => p.mode === aspectMode)!;
	// "free" → undefined (no constraint), "original" → image natural ratio, others → fixed
	const reactCropAspect =
		aspectMode === 'free'
			? undefined
			: aspectMode === 'original'
				? imgRef.current
					? imgRef.current.naturalWidth / imgRef.current.naturalHeight
					: undefined
				: (currentPreset.value ?? undefined);

	/* ── Entrance animation ── */
	useEffect(() => {
		const raf = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(raf);
	}, []);

	/** Convert a PercentCrop to PixelCrop based on displayed image dimensions */
	const percentToPixel = useCallback(
		(pc: PercentCrop, displayWidth: number, displayHeight: number): PixelCrop => ({
			unit: 'px',
			x: (pc.x / 100) * displayWidth,
			y: (pc.y / 100) * displayHeight,
			width: (pc.width / 100) * displayWidth,
			height: (pc.height / 100) * displayHeight,
		}),
		[],
	);

	/** Called when the image first loads — set initial crop to cover entire image */
	const onImageLoad = useCallback(
		(e: React.SyntheticEvent<HTMLImageElement>) => {
			const { width, height, naturalWidth, naturalHeight } = e.currentTarget;
			let pc: PercentCrop;
			if (aspectMode === 'free' || aspectMode === 'original') {
				pc = makeFullCrop(width, height);
			} else {
				const aspect = currentPreset.value ?? naturalWidth / naturalHeight;
				pc = makeCenteredAspectCrop(aspect, width, height);
			}
			setCrop(pc);
			setCompletedCrop(percentToPixel(pc, width, height));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const handleAspectModeChange = useCallback(
		(mode: AspectMode) => {
			setAspectMode(mode);
			const img = imgRef.current;
			if (!img) return;

			const { width, height, naturalWidth, naturalHeight } = img;
			const preset = ASPECT_PRESETS.find((p) => p.mode === mode)!;

			if (mode === 'free') {
				// Keep current crop, just remove aspect constraint
				return;
			}
			if (mode === 'original') {
				const pc = makeFullCrop(width, height);
				setCrop(pc);
				setCompletedCrop(percentToPixel(pc, width, height));
				return;
			}
			const aspect = preset.value ?? naturalWidth / naturalHeight;
			const pc = makeCenteredAspectCrop(aspect, width, height);
			setCrop(pc);
			setCompletedCrop(percentToPixel(pc, width, height));
		},
		[percentToPixel],
	);

	const _handleReset = useCallback(() => {
		const img = imgRef.current;
		if (!img) return;
		const { width, height, naturalWidth, naturalHeight } = img;

		if (aspectMode === 'free' || aspectMode === 'original') {
			const pc = makeFullCrop(width, height);
			setCrop(pc);
			setCompletedCrop(percentToPixel(pc, width, height));
		} else {
			const aspect = currentPreset.value ?? naturalWidth / naturalHeight;
			const pc = makeCenteredAspectCrop(aspect, width, height);
			setCrop(pc);
			setCompletedCrop(percentToPixel(pc, width, height));
		}
	}, [aspectMode, currentPreset.value, percentToPixel]);

	const handleConfirm = useCallback(async () => {
		if (!completedCrop?.width || !completedCrop.height) return;
		const img = imgRef.current;
		if (!img) return;
		setProcessing(true);
		try {
			const blob = await getCroppedBlob(imageSrc, completedCrop, img.width, img.height);
			const file = new File([blob], 'cover.jpg', { type: 'image/jpeg' });
			const previewUrl = URL.createObjectURL(blob);
			onConfirm(file, previewUrl);
		} catch {
			onCancel();
		} finally {
			setProcessing(false);
		}
	}, [imageSrc, completedCrop, onConfirm, onCancel]);

	/* ── Transition classes ── */
	const overlayClass = closing ? 'opacity-0' : entered ? 'opacity-100' : 'opacity-0';

	const panelClass = closing ? 'opacity-0 scale-95 translate-y-2' : entered ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4';

	return createPortal(
		<>
			{/* Backdrop */}
			<div
				aria-hidden
				className={`fixed inset-0 bg-black/40 backdrop-blur-md transition-all duration-300 ${overlayClass}`}
				onClick={requestClose}
				style={{ zIndex: Z.MODAL_OVERLAY + 10 }}
			/>

			{/* Modal */}
			<div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT + 10 }}>
				<div className={`liquid-glass-panel relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 ${panelClass}`}>
					{/* Header */}
					<div className="flex items-center justify-between px-5 py-3 border-b border-border/20">
						<div className="flex items-center gap-2">
							<div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10">
								<CropIcon className="h-3.5 w-3.5 text-primary" />
							</div>
							<h2 className="text-sm font-semibold text-foreground">裁剪封面</h2>
						</div>
						<button
							className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-foreground/5 text-muted-foreground hover:text-foreground transition-colors"
							onClick={requestClose}
							type="button"
						>
							<X className="h-4 w-4" />
						</button>
					</div>

					{/* Cropper area */}
					<div className="px-4 pt-3 pb-2">
						<div className="relative w-full rounded-xl overflow-hidden bg-black/5 flex items-center justify-center" style={{ minHeight: 200, maxHeight: 360 }}>
							<ReactCrop
								aspect={reactCropAspect}
								className="cover-crop-area"
								crop={crop}
								keepSelection
								minHeight={30}
								minWidth={30}
								onChange={(c) => setCrop(c)}
								onComplete={(c) => setCompletedCrop(c)}
								ruleOfThirds
							>
								<img alt="裁剪预览" crossOrigin="anonymous" onLoad={onImageLoad} ref={imgRef} src={imageSrc} style={{ maxHeight: 360, objectFit: 'contain' }} />
							</ReactCrop>
						</div>
					</div>

					{/* Aspect ratio selector */}
					<div className="flex items-center justify-center gap-1.5 px-5 py-2 overflow-x-auto">
						<span className="text-micro text-muted-foreground/40 mr-0.5 shrink-0">比例</span>
						{ASPECT_PRESETS.map((preset) => (
							<button
								className={`px-2.5 py-1 rounded-full text-micro font-medium transition-all shrink-0 ${
									aspectMode === preset.mode
										? 'bg-primary/10 text-primary ring-1 ring-primary/20'
										: 'text-muted-foreground/50 hover:text-foreground hover:bg-foreground/[0.04]'
								}`}
								key={preset.mode}
								onClick={() => handleAspectModeChange(preset.mode)}
								type="button"
							>
								{preset.label}
							</button>
						))}
					</div>

					{/* Tip */}
					<p className="text-micro text-muted-foreground/40 text-center px-5 pb-2">拖动裁剪框调整位置，拖动边角调整大小</p>

					{/* Actions */}
					<div className="flex items-center gap-3 px-5 pb-4 pt-1">
						<button
							className="flex-1 px-4 py-2 rounded-full border border-border/30 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-all disabled:opacity-50"
							disabled={processing}
							onClick={requestClose}
							type="button"
						>
							取消
						</button>
						<button
							className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
							disabled={processing || !completedCrop?.width || !completedCrop?.height}
							onClick={handleConfirm}
							type="button"
						>
							{processing ? (
								<>
									<div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
									处理中…
								</>
							) : (
								<>
									<Check className="h-3.5 w-3.5" />
									确认裁剪
								</>
							)}
						</button>
					</div>
				</div>
			</div>
		</>,
		document.body,
	);
}
